import { useState } from 'react';
import { ScanSearch, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { AgenteIaContextoSeletor, type AgenteIaContexto } from '@/components/agentes-ia/AgenteIaContextoSeletor';
import { useAgenteIaJob } from '@/hooks/useAgenteIaJob';
import { useAuditor360Resultado } from '@/hooks/useAuditor360';
import { riscoBadgeVariant, conformidadeStatusVariant } from '@/lib/riskBadge';
import type { ConcorrenteHabilitado, Classificacao, FaseDisputa } from '@/types/agentesIa';
import { useViewModeStore } from '@/stores/view-mode';

function EmptyState({ message }: { message: string }) {
  return <p className="text-sm text-ds-text-secondary italic text-center py-8">{message}</p>;
}

const selectClassName =
  'flex w-full rounded-lg border border-ds-border bg-ds-surface2 px-3 py-2.5 text-sm text-ds-text-primary ' +
  'focus:outline-none focus:ring-2 focus:ring-ds-brand/50 focus:border-ds-brand/50';

const CLASSIFICACAO_OPTIONS: { value: Classificacao; label: string }[] = [
  { value: 'primeiro', label: 'Primeiro' },
  { value: 'segundo', label: 'Segundo' },
  { value: 'terceiro', label: 'Terceiro' },
  { value: 'demais', label: 'Demais' },
  { value: 'nao_classificado', label: 'Não classificado' },
];

function novoConcorrente(): ConcorrenteHabilitado {
  return { nome: '' };
}

function Auditor360() {
  const searchParams = new URLSearchParams(window.location.search);
  const initialEmpresaId = Number(searchParams.get('empresaId')) || undefined;
  const initialLicitacaoId = Number(searchParams.get('licitacaoId')) || undefined;
  const { mode, previewEmpresaId } = useViewModeStore();
  const isEmpresaContextual = mode === 'empresa' && initialEmpresaId === previewEmpresaId && Boolean(initialLicitacaoId);
  const [contexto, setContexto] = useState<AgenteIaContexto | null>(null);
  const [fase, setFase] = useState<FaseDisputa>('pre_disputa');
  const [concorrentes, setConcorrentes] = useState<ConcorrenteHabilitado[]>([novoConcorrente()]);

  const preHook = useAgenteIaJob('auditor-360', contexto?.empresaId);
  const posHook = useAgenteIaJob('auditor-360-pos-disputa', contexto?.empresaId);
  const ativo = fase === 'pos_disputa' ? posHook : preHook;

  const { data: resultado, isLoading: resultadoLoading } = useAuditor360Resultado(
    contexto?.empresaId,
    contexto?.licitacaoId,
    fase,
    ativo.jobStatus?.status
  );

  const isRunning = Boolean(ativo.jobStatus && ativo.jobStatus.status !== 'completed' && ativo.jobStatus.status !== 'failed');
  const concorrentesValidos = concorrentes.filter((c) => c.nome.trim().length > 0);
  const bloqueadoPosDisputa = fase === 'pos_disputa' && concorrentesValidos.length === 0;

  const handleRodar = async () => {
    if (!contexto) return;
    ativo.reset();
    if (fase === 'pos_disputa') {
      await posHook.enqueue({
        licitacaoId: contexto.licitacaoId,
        documentoId: contexto.documentoId,
        concorrentesHabilitados: concorrentesValidos,
      });
    } else {
      await preHook.enqueue({
        licitacaoId: contexto.licitacaoId,
        documentoId: contexto.documentoId,
        fase: 'pre_disputa',
      });
    }
  };

  const atualizarConcorrente = (index: number, patch: Partial<ConcorrenteHabilitado>) => {
    setConcorrentes((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const removerConcorrente = (index: number) => {
    setConcorrentes((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ds-text-primary flex items-center gap-2">
          <ScanSearch className="h-6 w-6 text-ds-brand" />
          Auditor 360°
        </h1>
        <p className="text-ds-text-secondary mt-1">
          Auditoria de conformidade documental e de habilitação, antes e depois da disputa.
        </p>
      </div>

      <AgenteIaContextoSeletor onContextoCompleto={setContexto} initialEmpresaId={initialEmpresaId} initialLicitacaoId={initialLicitacaoId} contextoTravado={isEmpresaContextual} empresaRestritaId={mode === 'empresa' ? previewEmpresaId ?? undefined : undefined} />

      <Card>
        <CardHeader>
          <CardTitle>Rodar auditoria</CardTitle>
          <CardDescription>Escolha a fase da auditoria.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="inline-flex rounded-lg border border-ds-border overflow-hidden">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium transition-colors ${fase === 'pre_disputa' ? 'bg-ds-brand-subtle/50 text-ds-brand' : 'text-ds-text-secondary hover:bg-ds-surface2'}`}
              onClick={() => setFase('pre_disputa')}
            >
              Pré-disputa
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium transition-colors border-l border-ds-border ${fase === 'pos_disputa' ? 'bg-ds-brand-subtle/50 text-ds-brand' : 'text-ds-text-secondary hover:bg-ds-surface2'}`}
              onClick={() => setFase('pos_disputa')}
            >
              Pós-disputa
            </button>
          </div>

          {fase === 'pos_disputa' && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-ds-text-primary">Concorrentes habilitados</h4>
              {concorrentes.map((concorrente, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto] gap-3 items-end">
                  <Input
                    label={index === 0 ? 'Nome' : undefined}
                    placeholder="Nome do concorrente"
                    value={concorrente.nome}
                    onChange={(e) => atualizarConcorrente(index, { nome: e.target.value })}
                  />
                  <div>
                    {index === 0 && (
                      <label className="block text-sm font-medium text-ds-text-secondary mb-2">Preço ofertado</label>
                    )}
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={concorrente.preco_ofertado ?? ''}
                      onChange={(e) => atualizarConcorrente(index, {
                        preco_ofertado: e.target.value ? Number(e.target.value) : undefined,
                      })}
                    />
                  </div>
                  <div>
                    {index === 0 && (
                      <label className="block text-sm font-medium text-ds-text-secondary mb-2">Classificação</label>
                    )}
                    <select
                      className={selectClassName}
                      value={concorrente.classificacao ?? ''}
                      onChange={(e) => atualizarConcorrente(index, {
                        classificacao: (e.target.value || undefined) as Classificacao | undefined,
                      })}
                    >
                      <option value="">-</option>
                      {CLASSIFICACAO_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removerConcorrente(index)}
                    disabled={concorrentes.length === 1}
                  >
                    <Trash2 className="h-4 w-4 text-ds-danger" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setConcorrentes((prev) => [...prev, novoConcorrente()])}
              >
                <Plus className="h-4 w-4 mr-1" />
                Adicionar concorrente
              </Button>
              {bloqueadoPosDisputa && (
                <p className="text-sm text-ds-warning">Informe ao menos um concorrente habilitado.</p>
              )}
            </div>
          )}

          <Button
            onClick={handleRodar}
            disabled={!contexto || preHook.isEnqueuing || posHook.isEnqueuing || isRunning || bloqueadoPosDisputa}
            isLoading={preHook.isEnqueuing || posHook.isEnqueuing || isRunning}
          >
            {preHook.isEnqueuing || posHook.isEnqueuing || isRunning ? 'Rodando auditoria...' : 'Rodar auditoria'}
          </Button>

          {ativo.jobStatus?.status === 'failed' && (
            <div className="flex items-start gap-3 rounded-lg border border-ds-danger/20 bg-ds-danger/10 p-4">
              <AlertTriangle className="h-5 w-5 text-ds-danger shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-ds-danger">Falha ao rodar auditoria</p>
                <p className="text-sm text-ds-text-secondary mt-1">{ativo.jobStatus.failedReason}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Resultado — {fase === 'pos_disputa' ? 'Pós-disputa' : 'Pré-disputa'}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col">
          {!contexto ? (
            <EmptyState message="Selecione empresa, licitação e documento para ver a auditoria." />
          ) : resultadoLoading ? (
            <p className="text-center text-ds-text-secondary py-8">Carregando...</p>
          ) : !resultado ? (
            <EmptyState
              message={fase === 'pos_disputa' ? 'Nenhuma auditoria pós-disputa ainda.' : 'Nenhuma auditoria pré-disputa ainda.'}
            />
          ) : (
            <>
              <Table className="order-2">
                <TableHeader>
                  <TableRow>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Requisito</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Risco</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resultado.conformidade.map((item, index) => (
                    <TableRow key={`${item.requisito}-${index}`}>
                      <TableCell>{item.categoria}</TableCell>
                      <TableCell>{item.requisito}</TableCell>
                      <TableCell>
                        <Badge variant={conformidadeStatusVariant(item.status)} size="sm">{item.status}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={riscoBadgeVariant(item.risco)} size="sm">{item.risco}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {resultado.inconsistencias.length > 0 && (
                <div className="order-1 mb-6 space-y-3">
                  <h4 className="text-sm font-semibold text-ds-text-primary">Inconsistências</h4>
                  {resultado.inconsistencias.map((inconsistencia, index) => (
                    <div
                      key={`${inconsistencia.tipo}-${index}`}
                      className="rounded-lg border border-ds-border bg-ds-surface2 p-4"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-ds-text-primary">{inconsistencia.tipo}</span>
                        <Badge variant={riscoBadgeVariant(inconsistencia.severidade)} size="sm">
                          {inconsistencia.severidade}
                        </Badge>
                      </div>
                      <p className="text-sm text-ds-text-secondary mt-1">{inconsistencia.descricao}</p>
                      {inconsistencia.sugestao && (
                        <p className="text-sm text-ds-info mt-1">Sugestão: {inconsistencia.sugestao}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export { Auditor360 };
